import React, { useEffect, useState, useMemo } from "react";
import { Tag, Plus, Pencil, Trash2, X, Check, ChevronDown } from "lucide-react";
import { api } from "../api";
import { useToast } from "../hooks/use-toast";

/**
 * LeadSelector — campo "Lead" (origem do cliente: Instagram, Indicação, Google…)
 *
 * Props:
 *   - value: lead_id atual (string | null)
 *   - onChange(lead_id | null): callback quando seleciona/limpa
 *   - className: classes extras
 *   - compact: se true, usa layout compacto (button menor)
 *
 * Funcionalidades:
 *   • Selecionar Lead existente
 *   • Criar novo Lead inline ("+ Criar Lead")
 *   • Renomear Lead (Pencil)
 *   • Excluir Lead (Trash) — soft-delete se já tiver venda vinculada
 *
 * USO INTERNO ONLY. NÃO renderiza em recibos/PDFs/WhatsApp.
 */
export default function LeadSelector({ value, onChange, className = "", compact = false }) {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [search, setSearch] = useState("");
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/sale-leads");
      setLeads(r.data || []);
    } catch (e) {
      toast({ title: "Erro ao carregar Leads", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selected = useMemo(() => leads.find((l) => l.id === value) || null, [leads, value]);

  const filtered = useMemo(() => {
    if (!search) return leads;
    const q = search.toLowerCase();
    return leads.filter((l) => l.name.toLowerCase().includes(q));
  }, [leads, search]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const r = await api.post("/sale-leads", { name });
      await load();
      setNewName("");
      setCreating(false);
      onChange(r.data.id);
      toast({ title: "Lead criado", description: name });
    } catch (e) {
      toast({ title: "Erro ao criar Lead", variant: "destructive" });
    }
  };

  const handleSaveEdit = async () => {
    const name = editName.trim();
    if (!name || !editId) return;
    try {
      await api.put(`/sale-leads/${editId}`, { name });
      await load();
      setEditId(null);
      setEditName("");
      toast({ title: "Lead atualizado" });
    } catch (e) {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  const handleDelete = async (lead) => {
    const ok = window.confirm(
      `Excluir o Lead "${lead.name}"?\n\nSe houver vendas vinculadas, ele ficará Inativo (histórico preservado).`
    );
    if (!ok) return;
    try {
      const r = await api.delete(`/sale-leads/${lead.id}`);
      await load();
      if (value === lead.id) onChange(null);
      toast({
        title: r.data?.soft_deleted ? "Lead inativado" : "Lead excluído",
        description: r.data?.soft_deleted
          ? `${r.data.linked_records} venda(s) vinculadas — histórico preservado`
          : "Excluído permanentemente",
      });
    } catch (e) {
      toast({ title: "Erro ao excluir", variant: "destructive" });
    }
  };

  const btnClass = compact
    ? "h-9 px-2 text-xs"
    : "h-10 px-3 text-sm";

  return (
    <div className={`relative ${className}`} data-testid="lead-selector">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`${btnClass} w-full inline-flex items-center justify-between gap-2 rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 dark:bg-zinc-900 dark:border-zinc-700 dark:hover:bg-zinc-800 transition`}
        data-testid="lead-selector-button"
      >
        <span className="inline-flex items-center gap-2 truncate">
          <Tag size={14} className="text-zinc-500 shrink-0" />
          <span className={`truncate ${selected ? "text-zinc-900 dark:text-zinc-100" : "text-zinc-500"}`}>
            {selected ? selected.name : "Lead (origem do cliente)"}
          </span>
        </span>
        <ChevronDown size={14} className="text-zinc-500 shrink-0" />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => { setOpen(false); setCreating(false); setEditId(null); }}
          />
          <div className="absolute z-50 mt-1 w-full min-w-[260px] rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg max-h-80 overflow-hidden flex flex-col"
               data-testid="lead-selector-dropdown">
            {/* Search bar */}
            <div className="p-2 border-b border-zinc-200 dark:border-zinc-700">
              <input
                type="text"
                placeholder="Buscar Lead..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-8 px-2 text-xs rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
                data-testid="lead-selector-search"
              />
            </div>

            <div className="flex-1 overflow-auto">
              {/* "Sem Lead" option */}
              <button
                type="button"
                onClick={() => { onChange(null); setOpen(false); }}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 transition ${!value ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300" : ""}`}
                data-testid="lead-option-none"
              >
                <span className="text-zinc-500">— Sem Lead —</span>
              </button>

              {filtered.map((lead) => (
                <div key={lead.id} className="group flex items-center hover:bg-zinc-100 dark:hover:bg-zinc-800 transition">
                  {editId === lead.id ? (
                    <div className="flex-1 flex items-center gap-1 px-2 py-1.5">
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSaveEdit();
                          if (e.key === "Escape") { setEditId(null); setEditName(""); }
                        }}
                        className="flex-1 h-7 px-2 text-xs rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoFocus
                        data-testid="lead-edit-input"
                      />
                      <button
                        type="button"
                        onClick={handleSaveEdit}
                        className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-950 rounded"
                        data-testid="lead-edit-save"
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditId(null); setEditName(""); }}
                        className="p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => { onChange(lead.id); setOpen(false); }}
                        className={`flex-1 text-left px-3 py-2 text-xs ${value === lead.id ? "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 font-medium" : ""}`}
                        data-testid={`lead-option-${lead.id}`}
                      >
                        {lead.name}
                      </button>
                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 pr-2 transition">
                        <button
                          type="button"
                          onClick={() => { setEditId(lead.id); setEditName(lead.name); }}
                          className="p-1 text-zinc-500 hover:text-blue-600 rounded"
                          title="Renomear"
                          data-testid={`lead-edit-${lead.id}`}
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(lead)}
                          className="p-1 text-zinc-500 hover:text-red-600 rounded"
                          title="Excluir"
                          data-testid={`lead-delete-${lead.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {filtered.length === 0 && !loading && (
                <div className="px-3 py-3 text-xs text-zinc-500 italic">
                  {search ? "Nenhum Lead encontrado" : "Nenhum Lead cadastrado ainda"}
                </div>
              )}
            </div>

            {/* Create new lead area */}
            <div className="border-t border-zinc-200 dark:border-zinc-700 p-2">
              {creating ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreate();
                      if (e.key === "Escape") { setCreating(false); setNewName(""); }
                    }}
                    placeholder="Nome do novo Lead..."
                    className="flex-1 h-8 px-2 text-xs rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    autoFocus
                    data-testid="lead-create-input"
                  />
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={!newName.trim()}
                    className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-300 text-white rounded transition"
                    data-testid="lead-create-confirm"
                  >
                    Criar
                  </button>
                  <button
                    type="button"
                    onClick={() => { setCreating(false); setNewName(""); }}
                    className="p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="w-full inline-flex items-center gap-2 px-2 py-1.5 text-xs text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950 rounded transition"
                  data-testid="lead-create-button"
                >
                  <Plus size={14} />
                  Criar Lead
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
